import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import PortfolioPerformance from './PortfolioAnalysis/PortfolioPerformance';
import PortfolioRisk from './PortfolioAnalysis/PortfolioRisk';
import ExpertConsultation from './PortfolioAnalysis/ExpertConsultation';

const PortfolioAnalysis = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  
  // Touch/swipe state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSwipingActive, setIsSwipingActive] = useState(false);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const slides = [
    {
      title: "Evaluate your portfolio against market benchmarks",
      description: "Compare your investments with established market indices like Nifty 50 TRI",
      stats: {
        portfolio: "+22.11%",
        benchmark: "+16.58%"
      },
      component: PortfolioPerformance
    },
    {
      title: "Know your portfolio risks within minutes",
      description: "Get detailed analysis of your portfolio's risk metrics and diversification status across market caps",
      stats: {
        portfolio: "+20.31%",
        benchmark: "+16.58%"
      },
      component: PortfolioRisk
    },
    {
      title: "Get expert guidance through a personal consultation",
      description: "Schedule a 1-on-1 call with our investment experts to discuss your portfolio strategy",
      stats: {
        portfolio: "+13.30%",
        benchmark: "+16.58%"
      },
      component: ExpertConsultation
    }
  ];

  // Touch event handlers
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwipingActive(true);
    setIsPaused(true);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsSwipingActive(false);
      setIsPaused(false);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      const nextSlide = (activeSlide + 1) % slides.length;
      handleSlideChange(nextSlide);
    } else if (isRightSwipe) {
      const prevSlide = activeSlide === 0 ? slides.length - 1 : activeSlide - 1;
      handleSlideChange(prevSlide);
    } else {
      setIsPaused(false);
    }
    
    setIsSwipingActive(false);
  };

  // Function to start the auto-advance timer
  const startAutoAdvance = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      if (!isPaused && !isSwipingActive) {
        setActiveSlide((prev) => (prev + 1) % slides.length);
      }
    }, 5000);
  }, [isPaused, isSwipingActive, slides.length]);

  // Function to handle manual slide changes
  const handleSlideChange = useCallback((newIndex) => {
    setActiveSlide(newIndex);
    
    // Clear existing timers
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Pause briefly to let user see their selection, then resume
    setIsPaused(true);
    timeoutRef.current = setTimeout(() => {
      setIsPaused(false);
      startAutoAdvance();
    }, 2000);
  }, [startAutoAdvance]);

  // Handle mouse enter/leave for pause on hover (desktop only)
  const handleMouseEnter = useCallback(() => {
    if (!isMobile) {
      setIsPaused(true);
    }
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      setIsPaused(false);
    }
  }, [isMobile]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize auto-advance - starts immediately
  useEffect(() => {
    startAutoAdvance();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [startAutoAdvance]);

  const ActiveComponent = slides[activeSlide].component;

  return (
    <div className="w-full bg-gradient-to-b from-gray-50 to-white flex flex-col lg:flex-row gap-5 items-stretch">
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#113262] text-center pb-6 lg:pb-10">
          Get more out of your portfolio. With our free analysis.
        </h1>

        {/* For Mobile View */}
        {isMobile ? (
          <div className="space-y-6">
            {/* Swipeable content container */}
            <div 
              className="relative overflow-hidden"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ 
                  transform: `translateX(-${activeSlide * 100}%)`,
                  touchAction: 'pan-y pinch-zoom'
                }}
              >
                {slides.map((slide, index) => (
                  <div key={index} className="w-full flex-shrink-0 space-y-6">
                    <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 transition-all duration-300">
                      <h3 className="text-lg font-semibold text-[#113262]">
                        {slide.title}
                      </h3>
                      <p className="mt-2 text-sm text-[#545454]">
                        {slide.description}
                      </p>
                    </div>

                    {/* Active Component for Mobile */}
                    <div className="flex justify-center">
                      <slide.component stats={slide.stats} />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Visual feedback during swipe */}
              {isSwipingActive && (
                <div className="absolute inset-0 pointer-events-none">
                  <div className="h-full w-full bg-black bg-opacity-5 rounded-xl"></div>
                </div>
              )}
            </div>

            {/* Carousel Indicators */}
            <div className="flex justify-center gap-2">
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSlideChange(idx)}
                  className={`h-2.5 w-2.5 rounded-full transition-all duration-300 hover:scale-110 ${
                    activeSlide === idx ? 'bg-[#1C52A0]' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            {/* Swipe instruction text */}
            <div className="text-center">
              <a
              
                href="https://club.maxiomwealth.com/login"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full px-6 py-3 bg-gradient-to-r from-[#1C52A0] to-[#0A1E3A] text-white rounded-lg hover:scale-105 hover:shadow-xl shadow-lg transition-all duration-300 group"
                >
                <span className="font-semibold">Review my portfolio</span>
                <ArrowRight className="ml-3 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-200" />
              </a>
            </div>
          </div>
        ) : (
          // Desktop View
          <div 
            className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12 items-center"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="space-y-6 lg:space-y-8 w-full">
              <div className="space-y-6 relative">
                <div className="absolute left-0 h-full flex flex-col">
                  {[0, 1, 2].map((index) => (
                    <div
                      key={index}
                      className={`w-1 flex-1 transition-all duration-500 rounded-none ${
                        index === activeSlide ? 'bg-[#1C52A0]' : 'bg-[#E8EEF6]'
                      }`}
                    />
                  ))}
                </div>

                {slides.map((slide, index) => (
                  <div
                    key={index}
                    onClick={() => handleSlideChange(index)}
                    className={`pl-6 transition-all duration-500 ease-in-out cursor-pointer hover:opacity-90 ${
                      index === activeSlide
                        ? 'opacity-100 transform translate-y-0 scale-[1.02]'
                        : 'opacity-60 transform translate-y-2 hover:opacity-75'
                    }`}
                  >
                    <h3 className={`text-base sm:text-lg font-semibold transition-colors duration-300 ${
                      index === activeSlide ? 'text-[#113262]' : 'text-[#436FB0]'
                    }`}>
                      {slide.title}
                    </h3>
                    <p className={`mt-2 text-sm sm:text-base transition-colors duration-300 ${
                      index === activeSlide ? 'text-[#262626]' : 'text-[#545454]'
                    }`}>
                      {slide.description}
                    </p>
                  </div>
                ))}
              </div>

              <a 
                href="https://club.maxiomwealth.com/login" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-[#1C52A0] to-[#0A1E3A] text-white rounded-lg hover:scale-105 hover:shadow-xl shadow-lg transition-all duration-300 group"
              >
                <span className="font-semibold">Review my portfolio</span>
                <ArrowRight className="ml-3 h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-200" />
              </a>
            </div>

            {/* Active Component for Desktop */}
            <div className="flex justify-center">
              <ActiveComponent stats={slides[activeSlide].stats} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioAnalysis;